import { Inject } from '@odinjs/odin';

import { CONFIRMATION_FIELD_VALID, confirmationFieldValidator, Endpoint, passwordValidator, ProviderConfigurationService, PW_VALIDATOR, SSOService, ToastService } from '@philips-emr/hpms-provider-commons';
import { ButtonAPI, FieldAPI, FormAPI, ImageAPI, PanelAPI, TextAPI } from '@philips-emr/tws-components';
import { ToastLevel } from '@philips-emr/tws-constants';
import Controller, { OnClick, OnKeyDown, OnMount, OnValidate } from '@philips-emr/tws-core/lib/client';
import { ValidateEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService, ThemeService } from '@philips-emr/tws-core/lib/services';
import { HttpError } from '@philips-emr/tws-core/lib/services/http-service';

import { NewPwAliases as Aliases, FragmentNames } from '../codegen';
import { FOUNDATION_ROUTES } from '../route';

import BaseController from './BaseController';

@Controller({ fragment: FragmentNames.NEW_PW })
class NewPwController extends BaseController {

  @Inject(Aliases.PANEL_LOADER)
  private panelLoader: PanelAPI;

  @Inject(Aliases.PANEL_ACTIONS)
  private panelActions: PanelAPI;

  @Inject(Aliases.PANEL_SUCCESS)
  private panelSuccess: PanelAPI;

  @Inject(Aliases.PANEL_SCREEN)
  private panelScreen: PanelAPI;

  @Inject(Aliases.BUTTON_NEW_PW)
  private buttonNewPassword: ButtonAPI;

  @Inject(Aliases.FORM_NEW_PW)
  private formNewPassword: FormAPI;

  @Inject(Aliases.TEXT_MESSAGE)
  private textMessage: TextAPI;

  @Inject(Aliases.FIELD_NEW_PW)
  private fieldNewPassword: FieldAPI;

  @Inject(Aliases.FIELD_CONFIRM_PW)
  private fieldConfirmPassword: FieldAPI;

  @OnMount(Aliases.PANEL_SCREEN)
  onMountPanel(): void {
    SSOService.ssoEnabled();
  }

  @OnMount(Aliases.IMAGE_NEW_PW)
  onMountLogo(event: MouseEvent): void {
    ThemeService.setContentTonalRange(ProviderConfigurationService.getLoginTonalRange());
    const api = event.target as unknown as ImageAPI;

    ProviderConfigurationService.getConfigurationData()
      .then(data => api.setSrc(data.dsUrlLogoHeaderLogin as string))
      .catch(() => ToastService.open({ message: MessageService.translate('provider.error-process-request'), level: ToastLevel.ERROR }));
  }

  @OnValidate(Aliases.FORM_NEW_PW)
  onValidateForm(event: ValidateEvent): void {
    this.buttonNewPassword.setEnabled(event.valid);
  }

  @OnMount(Aliases.FORM_NEW_PW)
  onFormMount(): void {
    this.fieldConfirmPassword.addLocalValidator(CONFIRMATION_FIELD_VALID, (value) => {
      const confirmationValue = this.formNewPassword.getRecord()['newPassword'];
      return confirmationFieldValidator(value, confirmationValue);
    });
    this.fieldNewPassword.addLocalValidator(PW_VALIDATOR, passwordValidator);
  }

  @OnKeyDown([Aliases.INPUT_NEW_PW, Aliases.INPUT_CONFIRM_PW])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onClick();
    }
  }

  @OnClick(Aliases.BUTTON_NEW_PW)
  onClick(): void {
    if (this.formNewPassword.validate()) {
      this.panelLoader.setVisible(true);
      this.panelActions.setVisible(false);

      const { hash } = this.historyService.getCurrentRouteParams();

      const body = { ...this.formNewPassword.getRecord(), isUserConfirmation: this.isUserConfirmation() };

      HttpService.post(Endpoint.POST.NEW_PW, { hash }, { body })
        .then(() => {
          this.panelScreen.setVisible(false);
          this.panelSuccess.setVisible(true);
        })
        .catch(error => {
          this.panelLoader.setVisible(false);
          this.panelActions.setVisible(true);
          this.handleLoginError(error);
        });
    }
  }

  @OnClick(Aliases.BUTTON_OK)
  onBtnClick(): void {
    this.historyService.push(FOUNDATION_ROUTES.LOGIN);
  }

  private handleLoginError(error: HttpError): void {
    if (error.response) {
      error.response.json()
        .then(() => this.textMessage.setValue('provider.failed-generate-request'))
        .catch(() => this.textMessage.setValue('provider.failed-request'));
    } else {
      this.textMessage.setValue('provider.failed-generate-request');
    }
  }

  private isUserConfirmation(): boolean {
    const { path } = this.historyService.getMatch();
    return path.indexOf(FOUNDATION_ROUTES.CONFIRM_USER_NEW_PW) !== -1;
  }

}

export default NewPwController;
